import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';

/**
 * Ícono dentro de un círculo teñido. El tono sale de la paleta de la marca y de las series de los
 * gráficos, así un KPI y su gráfico comparten color. Siempre decorativo (`aria-hidden`): el texto de
 * al lado dice lo que es.
 */
export const iconBadgeVariants = cva(
  'inline-flex shrink-0 items-center justify-center rounded-full ring-1 ring-inset [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        brand: 'bg-highlight/10 text-highlight ring-highlight/15',
        cyan: 'bg-chart-1/12 text-chart-1 ring-chart-1/20',
        green: 'bg-chart-2/12 text-chart-2 ring-chart-2/20',
        violet: 'bg-chart-3/12 text-chart-3 ring-chart-3/20',
        orange: 'bg-chart-4/12 text-chart-4 ring-chart-4/20',
        navy: 'bg-brand-navy/8 text-brand-navy ring-brand-navy/12 dark:bg-accent dark:text-foreground dark:ring-border',
        success: 'bg-success/12 text-success ring-success/20',
        warning: 'bg-warning/12 text-warning ring-warning/20',
        destructive: 'bg-destructive/10 text-destructive ring-destructive/20',
        neutral: 'bg-muted text-muted-foreground ring-border',
      },
      size: {
        sm: 'size-7 [&_svg]:size-3.5',
        md: 'size-9 [&_svg]:size-[1.05rem]',
        lg: 'size-11 [&_svg]:size-5',
      },
    },
    defaultVariants: { tone: 'brand', size: 'md' },
  },
);

export interface IconBadgeProps
  extends ComponentProps<'span'>, VariantProps<typeof iconBadgeVariants> {}

export function IconBadge({ className, tone, size, ...props }: IconBadgeProps) {
  return (
    <span aria-hidden className={cn(iconBadgeVariants({ tone, size }), className)} {...props} />
  );
}
